import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { type FC, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { useLexicalContext } from "../../lexical-context";
import { useSelectable } from "../../utils/use-selectable";
import { $isSurveyAnswersNode } from "./helpers";

type StyleState = {
  questionBold: boolean;
  questionItalic: boolean;
  questionColor: string;
  answerBold: boolean;
  answerItalic: boolean;
  answerColor: string;
  divider: boolean;
};

export const SurveyAnswersComponent: FC<{
  nodeKey: string;
  questionBold: boolean;
  questionItalic: boolean;
  questionColor: string;
  answerBold: boolean;
  answerItalic: boolean;
  answerColor: string;
  divider: boolean;
}> = ({
  nodeKey,
  questionBold,
  questionItalic,
  questionColor,
  answerBold,
  answerItalic,
  answerColor,
  divider,
}) => {
  const [editor] = useLexicalComposerContext();
  const { format, triggerSurveyName } = useLexicalContext();
  const selected = useSelectable(nodeKey);
  const [hover, setHover] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pending, setPending] = useState<StyleState>({
    questionBold,
    questionItalic,
    questionColor,
    answerBold,
    answerItalic,
    answerColor,
    divider,
  });

  const handleOpen = (): void => {
    setPending({
      questionBold,
      questionItalic,
      questionColor,
      answerBold,
      answerItalic,
      answerColor,
      divider,
    });
    setModalOpen(true);
  };

  const handleConfirm = (): void => {
    editor.update(() => {
      const n = $getNodeByKey(nodeKey);
      if (!$isSurveyAnswersNode(n)) return;
      n.setQuestionBold(pending.questionBold);
      n.setQuestionItalic(pending.questionItalic);
      if (format === "email") n.setQuestionColor(pending.questionColor);
      n.setAnswerBold(pending.answerBold);
      n.setAnswerItalic(pending.answerItalic);
      if (format === "email") n.setAnswerColor(pending.answerColor);
      n.setDivider(pending.divider);
    });
    setModalOpen(false);
  };

  const handleDelete = (): void => {
    editor.update(() => {
      $getNodeByKey(nodeKey)?.remove();
    });
  };

  const questionStyle: React.CSSProperties = {
    fontWeight: questionBold ? "bold" : "normal",
    fontStyle: questionItalic ? "italic" : "normal",
    ...(format === "email" && { color: questionColor }),
  };

  const answerStyle: React.CSSProperties = {
    fontWeight: answerBold ? "bold" : "normal",
    fontStyle: answerItalic ? "italic" : "normal",
    ...(format === "email" && { color: answerColor }),
  };

  return (
    <>
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{
          width: "100%",
          p: 1,
          borderRadius: 1,
          outlineWidth: selected ? 2 : 1,
          outlineColor: "primary.main",
          outlineStyle: selected ? "solid" : "dotted",
          cursor: "default",
          userSelect: "none",
        }}
      >
        {/* Centered floating header chip */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 1,
            }}
          >
            <ChecklistIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">{triggerSurveyName ?? "Survey answers"}</Typography>
            <Tooltip title="This block will contain all of the responses from the person who submitted the survey. You can see the questions and placeholder answers when you review before publishing.">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
            <IconButton
              data-analytics-id="lexical-survey-answers-format"
              size="small"
              aria-label="Format survey answers"
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
            >
              <PaletteOutlinedIcon fontSize="small" />
            </IconButton>
            {(hover || selected) && editor.isEditable() && (
              <IconButton
                data-analytics-id="lexical-survey-answers-delete"
                size="small"
                aria-label="Delete survey answers"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Preview */}
        <Typography style={questionStyle}>Question text</Typography>
        <Typography style={answerStyle}>Response</Typography>
      </Box>

      {/* Formatting modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Formatting options</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Choose how questions and answers will be formatted.
          </Typography>

          {/* Question row */}
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography sx={{ minWidth: 120 }}>Question text</Typography>
            <IconButton
              data-analytics-id="lexical-survey-answers-bold-question"
              size="small"
              aria-label="Bold question text"
              onClick={() => setPending((p) => ({ ...p, questionBold: !p.questionBold }))}
              color={pending.questionBold ? "primary" : "default"}
            >
              <strong>B</strong>
            </IconButton>
            <IconButton
              data-analytics-id="lexical-survey-answers-italic-question"
              size="small"
              aria-label="Italic question text"
              onClick={() => setPending((p) => ({ ...p, questionItalic: !p.questionItalic }))}
              color={pending.questionItalic ? "primary" : "default"}
            >
              <em>I</em>
            </IconButton>
            {format === "email" && (
              <TextField
                aria-label="Question text colour"
                size="small"
                type="color"
                value={pending.questionColor}
                onChange={(e) => setPending((p) => ({ ...p, questionColor: e.target.value }))}
                sx={{ width: 80 }}
                slotProps={{
                  input: {
                    sx: { height: 40 },
                  },
                }}
              />
            )}
          </Stack>

          {/* Answer row */}
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Typography sx={{ minWidth: 120 }}>Answer text</Typography>
            <IconButton
              data-analytics-id="lexical-survey-answers-bold-answer"
              size="small"
              aria-label="Bold answer text"
              onClick={() => setPending((p) => ({ ...p, answerBold: !p.answerBold }))}
              color={pending.answerBold ? "primary" : "default"}
            >
              <strong>B</strong>
            </IconButton>
            <IconButton
              data-analytics-id="lexical-survey-answers-italic-answer"
              size="small"
              aria-label="Italic answer text"
              onClick={() => setPending((p) => ({ ...p, answerItalic: !p.answerItalic }))}
              color={pending.answerItalic ? "primary" : "default"}
            >
              <em>I</em>
            </IconButton>
            {format === "email" && (
              <TextField
                aria-label="Answer text colour"
                size="small"
                type="color"
                value={pending.answerColor}
                onChange={(e) => setPending((p) => ({ ...p, answerColor: e.target.value }))}
                sx={{ width: 80 }}
                slotProps={{
                  input: {
                    sx: { height: 40 },
                  },
                }}
              />
            )}
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={pending.divider}
                onChange={(e) => setPending((p) => ({ ...p, divider: e.target.checked }))}
              />
            }
            label="Include a divider after each answer"
          />
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="lexical-survey-answers-cancel"
            onClick={() => setModalOpen(false)}
            color="error"
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="lexical-survey-answers-confirm"
            onClick={handleConfirm}
            color="success"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
